import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ClockIcon, DollarSignIcon, ArrowRightIcon, ShieldIcon, BanknoteIcon, WalletIcon } from 'lucide-react';
type Step = 1 | 2 | 3;
export function SupplierPortal() {
  const supplierName = 'Premium Textiles Inc';
  const buyerName = 'Walmart Inc.';
  const invoiceId = 'INV-10231';
  const invoiceAmount = 165113;
  const dueDate = '2025-12-28';
  const terms = 'Net 60';
  const suggestedDiscount = 2.3;
  const [step, setStep] = useState<Step>(1);
  const [selectedOption, setSelectedOption] = useState<'wait' | 'early' | 'sell' | null>(null);
  const [destination, setDestination] = useState<'bank' | 'wallet'>('bank');
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    document.title = 'Mint | Supplier Funding';
  }, []);
  const formatAmount = (n: number) => n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
  const handleContinue = () => {
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2 && selectedOption) {
      setStep(3);
      return;
    }
    if (step === 3 && selectedOption) {
      setConfirmed(true);
    }
  };
  const primaryCtaLabel = step === 1 ? 'Continue' : step === 2 ? 'Continue' : confirmed ? 'Payment confirmed' : 'Confirm and get paid';
  const primaryDisabled = step === 2 && !selectedOption || step === 3 && !selectedOption;
  const daysToMaturity = 60;
  return <div className="min-h-screen bg-gray-50 px-6 py-6" data-id="element-2688">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-2689">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-2690">
          <div data-id="element-2691">
            <h1 className="text-3xl font-semibold text-gray-900" data-id="element-2692">
              Supplier Funding
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-2693">
              Accept early payment offers.
            </p>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2694">
          <KpiTile label="Eligible amount" value={formatAmount(invoiceAmount)} data-id="element-2695" />
          <KpiTile label="Early-pay discount" value={`${suggestedDiscount.toFixed(1)}%`} subtle="Supplier selected" data-id="element-2696" />
          <KpiTile label="Days to maturity" value={`${daysToMaturity} days`} subtle={`Due ${dueDate}`} data-id="element-2697" />
        </div>

        {/* Invite banner */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-2698">
          <div data-id="element-2699">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-2700">
              Invite
            </div>
            <div className="text-sm text-gray-900" data-id="element-2701">
              {buyerName} Treasury powered by Mint
            </div>
          </div>
          <div className="flex items-center text-xs text-gray-600" data-id="element-2702">
            <ShieldIcon size={14} className="mr-1 text-emerald-600" data-id="element-2703" />
            Secure link – no login required
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-2704">
          {/* Left: Invoice summary */}
          <div className="lg:col-span-1" data-id="element-2705">
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm h-full" data-id="element-2706">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-2707">
                Invoice from your ERP
              </div>
              <div className="mb-4" data-id="element-2708">
                <div className="text-sm text-gray-500" data-id="element-2709">Supplier</div>
                <div className="text-base font-semibold text-gray-900" data-id="element-2710">
                  {supplierName}
                </div>
              </div>
              <div className="space-y-2 text-sm" data-id="element-2711">
                <Row label="Buyer" value={buyerName} data-id="element-2712" />
                <Row label="Invoice ID" value={invoiceId} mono data-id="element-2713" />
                <Row label="Amount" value={formatAmount(invoiceAmount)} data-id="element-2714" />
                <Row label="Due date" value={dueDate} data-id="element-2715" />
                <Row label="Payment terms" value={terms} data-id="element-2716" />
              </div>
              <div className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-600" data-id="element-2717">
                This invoice was ingested directly from your accounting system
                and matched against Walmart&apos;s approved payables.
              </div>
            </div>
          </div>

          {/* Center: Options */}
          <div className="lg:col-span-1" data-id="element-2718">
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm h-full" data-id="element-2719">
              <div className="flex items-center justify-between mb-4" data-id="element-2720">
                <div data-id="element-2721">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-2722">
                    Step 2
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-2723">
                    Choose how you want to be paid
                  </h2>
                </div>
                <div className="text-xs text-gray-500 text-right" data-id="element-2724">
                  {formatAmount(invoiceAmount)} • due {dueDate}
                </div>
              </div>

              <div className="space-y-3" data-id="element-2725">
                <OptionCard selected={selectedOption === 'wait'} onClick={() => setSelectedOption('wait')} title="Wait for full payment at maturity" subtitle="Receive the full invoice amount on the contractual due date." badge="Standard" rightLabel={formatAmount(invoiceAmount)} data-id="element-2726" />
                <OptionCard selected={selectedOption === 'early'} onClick={() => setSelectedOption('early')} title="Get paid early at a small discount" subtitle={`Receive funds today at a ${suggestedDiscount.toFixed(1)}% early-payment discount.`} badge="Most selected" highlight rightLabel={formatAmount(Math.round(invoiceAmount * (1 - suggestedDiscount / 100)))} data-id="element-2727" />
                <OptionCard selected={selectedOption === 'sell'} onClick={() => setSelectedOption('sell')} title="Sell this invoice to investors" subtitle="List this receivable on Mint Marketplace for institutional bids." badge="Marketplace" rightLabel="Market price" data-id="element-2728" />
              </div>

              {confirmed && <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start" data-id="element-2729">
                  <CheckCircleIcon size={16} className="text-emerald-600 mt-0.5 mr-2" data-id="element-2730" />
                  <div className="text-xs text-emerald-800" data-id="element-2731">
                    Your selection is locked in. Funds will be routed according
                    to the option you chose and the payout destination below.
                  </div>
                </div>}
            </div>
          </div>

          {/* Right: Flow + destination + CTA */}
          <div className="lg:col-span-1" data-id="element-2732">
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm flex flex-col h-full" data-id="element-2733">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-3" data-id="element-2734">
                Flow
              </div>
              <div className="space-y-3 mb-6" data-id="element-2735">
                <StepItem index={1} label="Confirm invoice" active={step === 1} done={step > 1} data-id="element-2736" />
                <StepItem index={2} label="Choose payout option" active={step === 2} done={step > 2} data-id="element-2737" />
                <StepItem index={3} label="Select payout destination" active={step === 3} done={confirmed} data-id="element-2738" />
              </div>

              {/* Payout destination choices on step 3 */}
              {step >= 3 && <div className="mb-4" data-id="element-2739">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-2" data-id="element-2740">
                    Payout destination
                  </div>
                  <div className="grid grid-cols-1 gap-2" data-id="element-2741">
                    <DestinationCard label="Business bank account" description="Settle in your primary operating account." icon={<BanknoteIcon size={16} data-id="element-2743" />} selected={destination === 'bank'} onClick={() => setDestination('bank')} data-id="element-2742" />
                    <DestinationCard label="USDT wallet" description="Settle to a verified USDT address." icon={<WalletIcon size={16} data-id="element-2745" />} selected={destination === 'wallet'} onClick={() => setDestination('wallet')} data-id="element-2744" />
                  </div>
                </div>}

              <div className="mt-auto space-y-3" data-id="element-2746">
                {step === 3 && !confirmed && <div className="text-xs text-gray-600 border border-gray-200 rounded-md p-3 flex items-center" data-id="element-2747">
                    <ClockIcon size={14} className="text-gray-500 mr-2" data-id="element-2748" />
                    Typical time to fund is under 60 seconds after confirmation.
                  </div>}

                <button onClick={handleContinue} disabled={primaryDisabled || confirmed} className={`w-full inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium ${confirmed ? 'bg-gray-100 text-gray-600 border border-gray-200 cursor-default' : primaryDisabled && step !== 1 ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-2749">
                  {primaryCtaLabel}
                  {!confirmed && <ArrowRightIcon size={14} className="ml-1" data-id="element-2750" />}
                </button>

                <div className="text-[11px] text-gray-500 text-center" data-id="element-2751">
                  Powered by Mint. Settlement is fully automated and
                  institutionally funded; no credit checks or personal
                  guarantees.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* helpers */
function KpiTile({
  label,
  value,
  subtle
}: {
  label: string;
  value: string;
  subtle?: string;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2752">
      <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1" data-id="element-2753">
        {label}
      </div>
      <div className="text-xl font-semibold text-gray-900" data-id="element-2754">{value}</div>
      {subtle && <div className="text-[11px] text-gray-500 mt-1" data-id="element-2755">{subtle}</div>}
    </div>;
}
function Row({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div className="flex items-center justify-between text-sm" data-id="element-2756">
      <span className="text-gray-500" data-id="element-2757">{label}</span>
      <span className={`ml-3 text-gray-900 font-medium ${mono ? 'font-mono text-xs' : ''}`} data-id="element-2758">
        {value}
      </span>
    </div>;
}
function OptionCard({
  selected,
  onClick,
  title,
  subtitle,
  badge,
  highlight,
  rightLabel
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  badge: string;
  highlight?: boolean;
  rightLabel: string;
}) {
  return <button type="button" onClick={onClick} className={`w-full text-left border rounded-md p-3 flex items-start justify-between transition-colors ${selected ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-2759">
      <div data-id="element-2760">
        <div className="flex items-center gap-2 mb-1" data-id="element-2761">
          <span className="text-sm font-semibold text-gray-900" data-id="element-2762">{title}</span>
          <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${highlight ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-2763">
            {badge}
          </span>
        </div>
        <div className="text-xs text-gray-600" data-id="element-2764">{subtitle}</div>
      </div>
      <div className="ml-3 text-right flex flex-col items-end justify-center" data-id="element-2765">
        <div className="text-xs text-gray-500 mb-1" data-id="element-2766">You receive</div>
        <div className="text-sm font-semibold text-gray-900 flex items-center justify-end" data-id="element-2767">
          <DollarSignIcon size={14} className="mr-0.5 text-gray-500" data-id="element-2768" />
          <span data-id="element-2769">{rightLabel.replace('$', '')}</span>
        </div>
      </div>
    </button>;
}
function StepItem({
  index,
  label,
  active,
  done
}: {
  index: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return <div className="flex items-center" data-id="element-2770">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium mr-3 ${done ? 'bg-emerald-500 text-white' : active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-2771">
        {done ? <CheckCircleIcon size={12} data-id="element-2772" /> : index}
      </div>
      <div className="text-xs text-gray-800" data-id="element-2773">{label}</div>
    </div>;
}
function DestinationCard({
  label,
  description,
  icon,
  selected,
  onClick
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`w-full text-left border rounded-md p-3 flex items-start gap-3 transition-colors ${selected ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-2774">
      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${selected ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-2775">
        {icon}
      </div>
      <div data-id="element-2776">
        <div className="text-xs font-semibold text-gray-900" data-id="element-2777">{label}</div>
        <div className="text-[11px] text-gray-600 mt-0.5" data-id="element-2778">{description}</div>
      </div>
    </button>;
}